import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Scenario, Objective } from '@/engine/types';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Ico, ic, L } from '@/design-system/icons';
import { Modal, Btn, useToast } from '@/design-system';
import { useBreakpoint } from '@/design-system/hooks/useBreakpoint';
import { useDataStore } from '@/stores/data-store';
import { useAuthStore } from '@/stores/auth-store';
import { getRepository } from '@/data/repository-factory';
import { createScenario } from '@/engine/factories';
import { ComparePickerModal, type CompareTarget } from './ComparePickerModal';
import { CreateScenarioModal } from './CreateScenarioModal';
import { ScenarioNameModal } from './ScenarioNameModal';
import { uniqueCopyName } from '../scenario-utils';

type SubSpace = 'real' | 'scenario' | 'snapshot';

function useSubSpace(): { subSpace: SubSpace; entityId: string | null } {
  const { pathname } = useLocation();

  const scenarioMatch = pathname.match(/^\/budget\/scenarios\/([^/]+)/);
  if (scenarioMatch) return { subSpace: 'scenario', entityId: scenarioMatch[1] };

  const snapshotMatch = pathname.match(/^\/budget\/historique\/([^/]+)/);
  if (snapshotMatch) return { subSpace: 'snapshot', entityId: snapshotMatch[1] };

  return { subSpace: 'real', entityId: null };
}

const MAX_SCENARIOS = 5;

export function BudgetContextMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile } = useBreakpoint();

  const { subSpace, entityId } = useSubSpace();

  const dataStore = useDataStore();
  const currentUser = useAuthStore(s => s.currentUser);
  const scenarios = dataStore.scenarios as unknown as Scenario[];
  const objectives = dataStore.objectives as unknown as Objective[];
  const foyer = dataStore.foyer;

  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const scenario = subSpace === 'scenario' && entityId
    ? scenarios.find(s => s.id === entityId) ?? null
    : null;

  const atLimit = scenarios.length >= MAX_SCENARIOS;

  // Close on click outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [menuOpen]);

  // --- Action handlers ---

  const handleCompareSelect = (target: CompareTarget) => {
    const currentId = subSpace === 'real' ? 'real' : entityId ?? 'real';
    navigate(`/budget/comparer/${currentId}/${target.id}`);
  };

  const handleCreateScenario = async (name: string) => {
    if (!foyer) return;
    const repo = getRepository();

    const activeObjectives = objectives.filter(o => o.state === 'active');
    const projections = activeObjectives.flatMap(obj =>
      obj.projections
        .filter(p => p.budgetId === 'real')
        .map(p => ({ ...p, budgetId: '' })),
    );

    const newScenario = createScenario({ foyer, user: currentUser, name });

    if (projections.length > 0) {
      const updatedObjectives = objectives.map(obj => {
        if (obj.state !== 'active') return obj;
        const realProj = obj.projections.filter(p => p.budgetId === 'real');
        if (realProj.length === 0) return obj;
        const newProjs = realProj.map(p => ({
          ...p,
          budgetId: newScenario.id,
        }));
        return {
          ...obj,
          projections: [...obj.projections, ...newProjs],
          updatedAt: new Date().toISOString(),
        };
      });
      for (const obj of updatedObjectives) {
        if (obj !== objectives.find(o => o.id === obj.id)) {
          await repo.saveObjective(obj);
        }
      }
      dataStore.setObjectives(updatedObjectives as unknown as Parameters<typeof dataStore.setObjectives>[0]);
    }

    await repo.saveScenario(newScenario);
    dataStore.setScenarios([...scenarios, newScenario] as unknown as Parameters<typeof dataStore.setScenarios>[0]);
    navigate(`/budget/scenarios/${newScenario.id}`);
  };

  // La duplication demande désormais son nom (modale pré-remplie « Nom (2) »).
  const handleDuplicate = async (name: string) => {
    if (!scenario || !foyer) return;
    const repo = getRepository();
    const newScenario = createScenario({
      foyer,
      user: currentUser,
      name,
      diff: JSON.parse(JSON.stringify(scenario.diff)),
    });
    await repo.saveScenario(newScenario);
    dataStore.setScenarios([...scenarios, newScenario] as unknown as Parameters<typeof dataStore.setScenarios>[0]);
    navigate(`/budget/scenarios/${newScenario.id}`);
  };

  const handleRename = async (name: string) => {
    if (!scenario) return;
    const repo = getRepository();
    const updated = { ...scenario, name, updatedAt: new Date().toISOString() };
    await repo.saveScenario(updated);
    dataStore.setScenarios(
      scenarios.map(s => (s.id === scenario.id ? updated : s)) as unknown as Parameters<typeof dataStore.setScenarios>[0],
    );
  };

  const handleDelete = async () => {
    if (!scenario) return;
    const repo = getRepository();
    await repo.deleteScenario(scenario.id);
    const updated = scenarios.filter(s => s.id !== scenario.id);
    dataStore.setScenarios(updated as unknown as Parameters<typeof dataStore.setScenarios>[0]);
    toast({ message: t('budget.scenario.deleted'), level: 'success' });
    navigate('/budget/scenarios');
  };

  // --- Menu item style helper ---
  const itemStyle = (danger?: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 14px',
    fontFamily: sans,
    fontSize: 13,
    fontWeight: 500,
    color: danger ? P.neg : P.t1,
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    textAlign: 'left',
    cursor: 'pointer',
    outline: 'none',
  });

  return (
    <>
      <div ref={menuRef} style={{ position: 'relative' }} data-testid="budget-context-menu">
        <button
          type="button"
          aria-label={t('budget.contextMenu.aria')}
          onClick={() => setMenuOpen(o => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 44 : 32,
            height: isMobile ? 44 : 32,
            background: P.s2,
            border: `1px solid ${P.border}`,
            borderRadius: 6,
            cursor: 'pointer',
            outline: 'none',
          }}
          data-testid="ctx-menu-trigger"
          aria-expanded={menuOpen}
        >
          <Ico paths={[ic.more]} size={18} color={P.t1} sw={2.5} />
        </button>

        {menuOpen && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: isMobile ? 48 : 32,
              right: 0,
              zIndex: 50,
              background: P.s2,
              border: `1px solid ${P.border}`,
              borderRadius: 6,
              padding: 4,
              minWidth: 220,
              boxShadow: '0 4px 16px hsla(0,0%,0%,0.4)',
            }}
          >
            {/* Compare -- always visible */}
            <button
              type="button"
              role="menuitem"
              data-testid="ctx-compare"
              onClick={() => { setMenuOpen(false); setPickerOpen(true); }}
              style={itemStyle()}
            >
              <Ico paths={L.arrowLeftRight} size={16} color={P.t2} sw={1.75} />
              {t('budget.contextMenu.compare')}
            </button>

            {/* Duplicate as scenario -- real budget only */}
            {subSpace === 'real' && (
              <button
                type="button"
                role="menuitem"
                data-testid="ctx-duplicate-scenario"
                onClick={() => { setMenuOpen(false); setCreateOpen(true); }}
                disabled={atLimit}
                style={{
                  ...itemStyle(),
                  color: atLimit ? P.disabled : P.t1,
                  cursor: atLimit ? 'not-allowed' : 'pointer',
                }}
              >
                <Ico paths={L.copy} size={16} color={atLimit ? P.disabled : P.t2} sw={1.75} />
                {t('budget.contextMenu.duplicateAsScenario')}
              </button>
            )}

            {/* Rename -- scenario only */}
            {subSpace === 'scenario' && (
              <button
                type="button"
                role="menuitem"
                data-testid="ctx-rename"
                onClick={() => { setMenuOpen(false); setRenameOpen(true); }}
                style={itemStyle()}
              >
                <Ico paths={[ic.edit]} size={16} color={P.t2} sw={1.75} />
                {t('budget.edit.rename')}
              </button>
            )}

            {/* Duplicate -- scenario only */}
            {subSpace === 'scenario' && (
              <button
                type="button"
                role="menuitem"
                data-testid="ctx-duplicate"
                onClick={() => { setMenuOpen(false); setDupOpen(true); }}
                style={itemStyle()}
              >
                <Ico paths={L.copy} size={16} color={P.t2} sw={1.75} />
                {t('budget.contextMenu.duplicate')}
              </button>
            )}

            {/* Delete -- scenario only */}
            {subSpace === 'scenario' && (
              <button
                type="button"
                role="menuitem"
                data-testid="ctx-delete"
                onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}
                style={itemStyle(true)}
              >
                <Ico paths={L.trash} size={16} color={P.neg} sw={1.75} />
                {t('budget.contextMenu.delete')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Compare picker */}
      <ComparePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentKind={subSpace === 'real' ? 'real' : subSpace}
        currentId={subSpace === 'real' ? 'real' : entityId ?? ''}
        onSelect={handleCompareSelect}
      />

      {/* Create scenario modal (for "Dupliquer en scenario" from real) */}
      <CreateScenarioModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreateScenario}
        existingNames={scenarios.map(s => s.name)}
        objectives={objectives}
      />

      {/* Rename / duplicate (scenario only) — modale de nom partagée */}
      {scenario && (
        <ScenarioNameModal
          open={renameOpen}
          title={t('budget.contextMenu.renameScenario')}
          initialName={scenario.name}
          existingNames={scenarios.filter(s => s.id !== scenario.id).map(s => s.name)}
          submitLabel={t('actions.validate')}
          onClose={() => setRenameOpen(false)}
          onSubmit={name => void handleRename(name)}
        />
      )}
      {scenario && (
        <ScenarioNameModal
          open={dupOpen}
          title={t('budget.contextMenu.duplicate')}
          initialName={uniqueCopyName(scenario.name, scenarios.map(s => s.name))}
          existingNames={scenarios.map(s => s.name)}
          submitLabel={t('actions.create')}
          onClose={() => setDupOpen(false)}
          onSubmit={name => void handleDuplicate(name)}
        />
      )}

      {/* Delete confirmation (scenario only) */}
      {subSpace === 'scenario' && scenario && (
        <Modal
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={t('budget.scenario.confirmDeleteTitle')}
          actions={
            <>
              <Btn variant="secondary" label={t('actions.cancel')} onClick={() => setConfirmDelete(false)} />
              <Btn variant="destructive" label={t('actions.delete')} onClick={handleDelete} />
            </>
          }
        >
          <p style={{ fontFamily: sans, fontSize: 13, color: P.t1, margin: 0, lineHeight: 1.5 }}>
            {t('budget.scenario.confirmDelete', { name: scenario.name })}
          </p>
        </Modal>
      )}
    </>
  );
}
